import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

type ToastKind = "info" | "success" | "warning" | "error";
type Toast = { id: number; kind: ToastKind; text: string; ttlMs?: number };

export const ToastCtx = createContext<{ push: (t: Omit<Toast, "id">) => void } | null>(null);

type GlobalRootProps = {
  userName?: string;
  photoUrl?: string;
  containerEl?: HTMLElement | null;
};

export function GlobalRoot({ userName = "", photoUrl = "", containerEl }: GlobalRootProps): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = Date.now() + Math.random();
    const ttl = t.ttlMs ?? 3500;
    setToasts(prev => [...prev, { ...t, id }]);
    if (ttl > 0) setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), ttl);
  }, []);

  useEffect(() => {
    (window as any).GlobalUI = (window as any).GlobalUI || {};
    (window as any).GlobalUI.toast = (text: string, kind: ToastKind = "info", ttlMs?: number) =>
      push({ text, kind, ttlMs });
    return () => { if ((window as any).GlobalUI) delete (window as any).GlobalUI.toast; };
  }, [push]);

  const [collapsed, setCollapsed] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  // 親コンテナへ class 反映（querySelector 撤去済）
  useEffect(() => {
    if (!containerEl) return;
    containerEl.classList.toggle("collapsed", collapsed);
  }, [collapsed, containerEl]);

  const userMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!userOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (userMenuRef.current?.contains(t)) return;
      setUserOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setUserOpen(false); };
    window.addEventListener("click", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [userOpen]);

  const ctx = useMemo(() => ({ push }), [push]);

  return (
    <ToastCtx.Provider value={ctx}>
      <LayoutHeader
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        userOpen={userOpen}
        setUserOpen={setUserOpen}
        userMenuRef={userMenuRef}
        userName={userName}
        photoUrl={photoUrl}
      />
      <ToastContainer
        items={toasts}
        onDismiss={id => setToasts(prev => prev.filter(t => t.id !== id))}
      />
    </ToastCtx.Provider>
  );
}

function LayoutHeader(props: {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  userOpen: boolean;
  setUserOpen: (v: boolean) => void;
  userMenuRef: React.RefObject<HTMLDivElement>;
  userName: string;
  photoUrl: string;
}): JSX.Element {
  return (
    <div
      className="layout-react-header d-flex align-items-center px-3 gap-3"
      style={{
        position: "relative",
        zIndex: 510,
        height: 40,
        background: "#0f172a",
        color: "#fff",
        fontSize: "0.75rem"
      }}
    >
      <button
        type="button"
        aria-label="サイドバー切替"
        className="btn btn-sm btn-secondary"
        onClick={() => props.setCollapsed(!props.collapsed)}
      >
        {props.collapsed ? "▶" : "◀"}
      </button>

      <div
        className="user-menu position-relative"
        ref={props.userMenuRef}
        style={{ marginLeft: "auto" }}
      >
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={props.userOpen ? "true" : "false"}
          className="btn btn-sm btn-outline-light d-flex align-items-center gap-2"
          onClick={() => props.setUserOpen(!props.userOpen)}
        >
          {props.photoUrl && (
            <img
              src={props.photoUrl}
              alt="avatar"
              style={{ width: 24, height: 24, objectFit: "cover", borderRadius: "50%" }}
            />
          )}
          <span className="small">{props.userName || "アカウント"}</span>
          <span aria-hidden="true">▾</span>
        </button>
        {props.userOpen && (
          <ul
            className="list-unstyled shadow-sm bg-white rounded py-1 px-2 position-absolute"
            role="menu"
            aria-label="ユーザーメニュー"
            style={{ top: "100%", right: 0, minWidth: 160 }}
          >
            <li>
              <a href="/profile/" className="dropdown-item small d-block py-1 px-2 text-decoration-none" role="menuitem">
                プロフィールの編集
              </a>
            </li>
            <li><hr className="my-1" /></li>
            <li>
              <a href="/logout/" className="dropdown-item small d-block py-1 px-2 text-decoration-none" role="menuitem">
                ログアウト
              </a>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

function ToastContainer(props: { items: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div
      className="global-toast-container"
      style={{
        position: "fixed",
        top: 12,
        right: 12,
        zIndex: 1050,
        display: "flex",
        flexDirection: "column",
        gap: 8
      }}
    >
      {props.items.map(t => (
        <div
          key={t.id}
          className={"toast shadow-sm text-white small " + kindClass(t.kind)}
          role="status"
          style={{ padding: "8px 10px", borderRadius: 6, minWidth: 240, maxWidth: 420 }}
        >
          <span>{t.text}</span>
          <button
            type="button"
            aria-label="閉じる"
            onClick={() => props.onDismiss(t.id)}
            style={{
              marginLeft: 8,
              background: "transparent",
              border: "none",
              color: "inherit",
              float: "right",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function kindClass(k: ToastKind) {
  switch (k) {
    case "success": return "bg-success";
    case "warning": return "bg-warning text-dark";
    case "error": return "bg-danger";
    default: return "bg-secondary";
  }
}